import { createFileRoute } from "@tanstack/react-router";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import {
  SmsConnectionDialogs,
  type SmsConnectionDialogCopy,
} from "./-components/sms-connection-dialogs";
import { SmsConnectionTable } from "./-components/sms-connection-table";
import { SmsMessageLog } from "./-components/sms-message-log";
import { SmsThreads } from "./-components/sms-threads";
import { useSmsConnections, type SmsConnectionToasts } from "./-components/use-sms-connections";
import { NeedsActivation } from "@/components/app/activation/needs-activation";

export const Route = createFileRoute("/_authenticated/_workforce/hiring/sms")({
  component: HiringSms,
});

const TOASTS: SmsConnectionToasts = {
  created: "Connection saved",
  updated: "Connection updated",
  removed: "Connection removed",
  testPassed: "Twilio accepted those credentials.",
  testFailed: "Twilio would not accept those credentials.",
};

const DIALOGS: SmsConnectionDialogCopy = {
  createTitle: "Add a connection",
  editTitle: null,
  removeTitle: (name) => <>Remove {name}?</>,
  removeDescription:
    "Nobody will be texted from this number again. Messages already sent keep their history.",
  keepLabel: "Keep it",
  removeLabel: "Remove it",
  removingLabel: null,
};

/** Per-building candidate texting: Twilio account, number, health and sent messages. One screen
 * because an empty inbox first raises "is it set up". */
function HiringSms() {
  const { facilityId } = useFacility();
  const sms = useSmsConnections(facilityId, TOASTS);
  const { canManage, connections, rows } = sms;
  const live = rows.filter((row) => row.isActive).length;

  return (
    <>
      <PageHeader
        title="Text messaging"
        meta={
          connections.isPending
            ? "Loading…"
            : rows.length === 0
              ? "No building can text a candidate yet."
              : `${rows.length} ${rows.length === 1 ? "connection" : "connections"}, ${live} in use`
        }
        actions={
          canManage ? (
            // Outline until one exists: the activation request below is the first step.
            <Button
              variant={rows.length > 0 ? "default" : "outline"}
              onClick={() => sms.setCreating(true)}
            >
              Add a connection
            </Button>
          ) : null
        }
      />

      {connections.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : connections.isError ? (
        <ReadFailed
          message="Could not load the connections."
          error={connections.error}
          onRetry={() => void connections.refetch()}
          retrying={connections.isFetching}
        />
      ) : rows.length === 0 ? (
        <div className="space-y-4">
          <NeedsActivation
            featureKey="texting"
            facilityId={facilityId === "all" ? null : facilityId}
          />
          <EmptyState
            title="No text messaging set up"
            description="A candidate is texted from their own building's number, so each building needs its own connection."
          />
        </div>
      ) : (
        <SmsConnectionTable {...sms.table} />
      )}

      <SmsThreads facilityId={facilityId} />

      <SmsMessageLog facilityId={facilityId} />

      <SmsConnectionDialogs sms={sms} copy={DIALOGS} />
    </>
  );
}
